import { Box, Divider, Paper, Typography } from '@mui/material';
import { HorizontalBarChart } from '@/components/charts/HorizontalBarChart';
import type {
  HorizontalBarDatum,
  ValueMode,
} from '@/components/charts/HorizontalBarChart';
import { WidgetTrendChartSkeleton } from '@/components/common/WidgetTrendChartSkeleton';
import { useLocalization } from '@/hooks/useLocalization';
import type { ChartCategoryPoint } from '@/models/ServiceTypes';
import { toChartData } from './chartData';
import { bucketLabel, type BucketKind } from './bucketLabels';

const CHART_HEIGHT = 260;
const NARROW_Y_AXIS_LABEL_WIDTH = 110;
const WIDE_Y_AXIS_LABEL_WIDTH = 150;

export interface ClientActivitySectionProps {
  title: string;
  subtitle: string;
  points: ChartCategoryPoint[] | undefined;
  isLoading: boolean;
  isError: boolean;
  valueMode: ValueMode;
  stacked: boolean;
  bucketKind: BucketKind;
}

/**
 * Tooltip for a bar, showing the client count and the share of the book it is.
 *
 * The chart's default tooltip formats the raw value as currency, which is right
 * for the balance charts it was built for and wrong for every section here:
 * these bars count people.
 */
function renderTooltip(datum: HorizontalBarDatum) {
  return (
    <Paper
      elevation={3}
      sx={{ p: 1, pointerEvents: 'none' }}
    >
      <Typography
        variant='body2'
        sx={{ fontWeight: 'bold' }}
      >
        {datum.name}
      </Typography>
      <Divider sx={{ my: 0.5 }} />
      <Typography variant='body2'>
        {`${datum.rawValue.toLocaleString()} (${Math.round(datum.percentValue).toString()}%)`}
      </Typography>
    </Paper>
  );
}

/**
 * One titled section of the Client Activity widget: a heading, the subtitle
 * that says what window the numbers cover, and a horizontal bar chart.
 *
 * An empty series renders the no-data message rather than an empty chart frame,
 * which is what an advisor with no clients, or no clients in any bucket, should
 * see (AC 3.1). A failed request is kept distinct from that: a request that
 * never landed also leaves no data behind, and reporting it as "no clients"
 * would make an unreachable backend look like an empty book.
 */
export const ClientActivitySection = ({
  title,
  subtitle,
  points,
  isLoading,
  isError,
  valueMode,
  stacked,
  bucketKind,
}: ClientActivitySectionProps) => {
  const { t } = useLocalization();
  const data = points
    ? toChartData(points, (point) =>
        bucketLabel(t, bucketKind, point.id, point.name),
      )
    : [];

  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        variant='subtitle1'
        sx={{ fontWeight: 600 }}
      >
        {title}
      </Typography>
      <Typography
        variant='body2'
        color='text.secondary'
        sx={{ marginBottom: 1 }}
      >
        {subtitle}
      </Typography>

      {isLoading ? <WidgetTrendChartSkeleton /> : null}

      {!isLoading && isError ? (
        <Box
          sx={{
            height: CHART_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography
            variant='body2'
            color='error'
          >
            {t('clientActivity.loadError', 'Could not load client data.')}
          </Typography>
        </Box>
      ) : null}

      {!isLoading && !isError && data.length === 0 ? (
        <Box
          sx={{
            height: CHART_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography
            variant='body2'
            color='text.secondary'
          >
            {t('clientActivity.noData', 'No client data available')}
          </Typography>
        </Box>
      ) : null}

      {!isLoading && !isError && data.length > 0 ? (
        <HorizontalBarChart
          data={data}
          valueMode={valueMode}
          height={CHART_HEIGHT}
          yAxisLabelWidth={
            stacked ? NARROW_Y_AXIS_LABEL_WIDTH : WIDE_Y_AXIS_LABEL_WIDTH
          }
          tooltipRenderer={renderTooltip}
          showValueLabels
        />
      ) : null}
    </Box>
  );
};

export default ClientActivitySection;
